"use client";

import { KeyboardEvent, useEffect, useCallback } from "react";
import { isMobile } from "../../utils";

type useMediaCarouselProps = {
  length: number;
  currentSlideIndex: number;
  handleSlideIndexChange: (value: number) => void;
  slideRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  buttonRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
};

export const useCarouselNavigation = ({
  length,
  slideRefs,
  buttonRefs,
  currentSlideIndex,
  handleSlideIndexChange,
}: useMediaCarouselProps) => {
  const mobileView = isMobile();

  useEffect(() => {
    slideRefs.current = slideRefs.current.slice(0, length);
    buttonRefs.current = buttonRefs.current.slice(0, length);
  }, [length]);

  const getSlideFocus = useCallback((index: number) => {
    const currentSlide = slideRefs.current[index];
    if (!currentSlide) return;
    const mediaCtaElement = currentSlide.querySelector(
      '[data-label="carousel-slide"]',
    );
    if (mediaCtaElement && mediaCtaElement instanceof HTMLElement) {
      const isVisible = !!(
        mediaCtaElement.offsetWidth ||
        mediaCtaElement.offsetHeight ||
        mediaCtaElement.getClientRects().length
      );
      if (isVisible) {
        mediaCtaElement.focus({ preventScroll: true });
      }
    }
  }, []);

  const navigateToSlide = useCallback(
    (newIndex: number) => {
      handleSlideIndexChange(newIndex);
      requestAnimationFrame(() => {
        getSlideFocus(newIndex);
      });
    },
    [getSlideFocus],
  );

  const goToPrevious = useCallback(() => {
    if (currentSlideIndex > 0) {
      const newIndex = currentSlideIndex - 1;
      navigateToSlide(newIndex);
    } else {
      navigateToSlide(length - 1);
    }
  }, [currentSlideIndex, getSlideFocus]);

  const goToNext = useCallback(() => {
    if (currentSlideIndex < length - 1) {
      const newIndex = currentSlideIndex + 1;
      navigateToSlide(newIndex);
    } else {
      navigateToSlide(0);
    }
  }, [currentSlideIndex, length, getSlideFocus]);

  const goToSlide = useCallback(
    (index: number) => {
      if (index !== currentSlideIndex) {
        navigateToSlide(index);
      }
    },
    [currentSlideIndex, length, getSlideFocus],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement | HTMLButtonElement>) => {
      switch (e.key) {
        case "ArrowLeft":
          goToPrevious();
          e.preventDefault();
          break;
        case "ArrowRight":
          goToNext();
          e.preventDefault();
          break;
        case "ArrowUp":
          if (mobileView) {
            goToPrevious();
            e.preventDefault();
          }
          break;
        case "ArrowDown":
          if (mobileView) {
            goToNext();
            e.preventDefault();
          }
          break;
        case "Home":
          goToSlide(0);
          e.preventDefault();
          break;
        case "End":
          goToSlide(length - 1);
          e.preventDefault();
          break;
        default:
          break;
      }
    },
    [goToNext, goToPrevious, goToSlide, length, mobileView],
  );

  const handleIndicatorKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (e.key === "Enter" || e.key === " ") {
        goToSlide(index);
        e.preventDefault();
      }
    },
    [goToSlide],
  );

  const handleIndicatorClick = useCallback(
    (index: number) => {
      goToSlide(index);
    },
    [goToSlide],
  );

  const handleIndicatorFocus = useCallback(
    (index: number) => {
      const button = buttonRefs.current[index];
      if (button) {
        const rect = button.getBoundingClientRect();
        const offsetTop = rect.top + window.scrollY;
        const navbarHeight = 60;

        if (rect.top < navbarHeight || rect.bottom > window.innerHeight) {
          window.scrollTo({
            top: offsetTop - navbarHeight - 50, // 10px padding
            behavior: "smooth",
          });
        }
      }
    },
    [buttonRefs],
  );

  useEffect(() => {
    slideRefs.current.forEach((slide, idx) => {
      if (!slide) return;

      const isCurrentSlide = idx === currentSlideIndex;
      slide.setAttribute("aria-hidden", isCurrentSlide ? "false" : "true");
      const focusableElements = slide.querySelectorAll(
        "a[href], button:not([disabled]), [tabindex]",
      );
      focusableElements.forEach((el) => {
        if (
          el.hasAttribute("data-skip-carousel-focus") ||
          el.getAttribute("aria-hidden") === "true"
        ) {
          return;
        }
        if (el instanceof HTMLElement) {
          const current = el.getAttribute("tabindex");
          const original = el.getAttribute("data-original-tabindex");
          if (isCurrentSlide) {
            if (original !== null) {
              if (original === "") {
                el.removeAttribute("tabindex");
              } else {
                el.setAttribute("tabindex", original);
              }
              el.removeAttribute("data-original-tabindex");
            }
          } else {
            if (current !== "-1") {
              el.setAttribute("data-original-tabindex", current ?? "");
              el.setAttribute("tabindex", "-1");
            }
          }
        }
      });
    });
  }, [currentSlideIndex, mobileView]);

  return {
    handleIndicatorClick,
    handleIndicatorKeyDown,
    handleKeyDown,
    handleIndicatorFocus,
    currentSlideIndex,
  };
};
